/*
 *  ************************************************************************
 *  *  © [2015 - 2026] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */

import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";

class ScrollItem extends React.Component {
  constructor(props) {
    super(props);
    this.node = null;
    this.state = {
      minHeight: props.minHeight || 50,
    };
  }

  componentDidMount() {
    this.observeAll(this.props.observers);
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.observers || [];
    const current = this.props.observers || [];

    prev.forEach((obs) => {
      if (obs && !current.includes(obs) && this.node) {
        obs.unobserve(this.node);
      }
    });

    current.forEach((obs) => {
      if (obs && !prev.includes(obs) && this.node) {
        obs.observe(this.node);
      }
    });
  }

  componentWillUnmount() {
    this.unobserveAll(this.props.observers);
  }

  observeAll(observers = []) {
    if (!this.node) return;
    observers.forEach((obs) => {
      if (obs && typeof obs.observe === "function") {
        obs.observe(this.node);
      }
    });
  }

  unobserveAll(observers = []) {
    if (!this.node) return;
    observers.forEach((obs) => {
      if (obs && typeof obs.unobserve === "function") {
        obs.unobserve(this.node);
      }
    });
  }

  render() {
    const { index, data, render, minHeight } = this.props;
    return (
      <div
        ref={(el) => {
          this.node = el;
        }}
        data-infinite-scroll={index}
        style={{ minHeight: this.state.minHeight || minHeight || 50 }}
      >
        {render({ index, ...data })}
      </div>
    );
  }
}

ScrollItem.propTypes = {
  index: PropTypes.number.isRequired,
  data: PropTypes.object,
  render: PropTypes.func.isRequired,
  minHeight: PropTypes.number,
  observers: PropTypes.array,
};

class ScrollLoadMore extends React.Component {
  constructor(props) {
    super(props);
    this.node = null;
  }

  componentDidMount() {
    if (this.props.observer && this.node) {
      this.props.observer.observe(this.node);
    }
  }

  componentWillUnmount() {
    if (this.props.observer && this.node) {
      this.props.observer.unobserve(this.node);
    }
  }

  render() {
    return (
      <div
        ref={(el) => {
          this.node = el;
        }}
        data-infinite-scroll="load-more"
      />
    );
  }
}

ScrollLoadMore.propTypes = {
  observer: PropTypes.object,
};

export class InfiniteStoryBase extends React.Component {
  constructor(props) {
    super(props);

    const primaryStoryId = get(props, ["data", "story", "id"]);
    this.state = {
      moreItems: [],
      loading: false,
      pageNumber: 0,
      seenStoryIds: primaryStoryId ? [primaryStoryId] : [],
      hasMore: true,
    };

    this.loadObserver = null;
    this.focusObserver = null;
    this.initObservers();
  }

  initObservers() {
    if (typeof window === "undefined" || !window.IntersectionObserver) return;

    // Load observer: watches the sentinel <ScrollLoadMore /> to trigger fetching
    this.loadObserver = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target.getAttribute("data-infinite-scroll") === "load-more" && entry.isIntersecting) {
            this.loadMore();
          }
        });
      },
      {
        rootMargin: this.props.loadMargin || "200px 0px 500px",
      }
    );

    // Focus observer: monitors which story is currently in focus for URL & analytics sync
    const focusCallbackAt = this.props.focusCallbackAt || 20;
    this.focusObserver = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const itemIndex = entry.target.getAttribute("data-infinite-scroll");
          if (entry.isIntersecting && itemIndex !== "load-more" && itemIndex !== null) {
            this.onFocus(parseInt(itemIndex, 10));
          }
        });
      },
      {
        rootMargin: `-${100 - focusCallbackAt}% 0px -${focusCallbackAt}%`,
      }
    );
  }

  terminateLoadObserver() {
    if (this.loadObserver) {
      this.loadObserver.disconnect();
      this.loadObserver = null;
    }
  }

  terminateFocusObserver() {
    if (this.focusObserver) {
      this.focusObserver.disconnect();
      this.focusObserver = null;
    }
  }

  componentWillUnmount() {
    this.terminateLoadObserver();
    this.terminateFocusObserver();
  }

  allItems() {
    return [this.props.data].concat(this.state.moreItems);
  }

  onFocus(index) {
    const item = this.allItems()[index];
    if (!item) return;

    const storyId = item?.story?.id;
    const isInitialFocus = storyId && !this.state.seenStoryIds.includes(storyId);

    // 1. Mark story as seen synchronously to guarantee back-and-forth scrolling never fires duplicate pageviews
    if (isInitialFocus) {
      this.state.seenStoryIds.push(storyId);
      this.setState({
        seenStoryIds: [...this.state.seenStoryIds],
      });
    }

    // 2. Synchronously update browser URL and document title via maybeSetUrl (which uses window.__nativePushState)
    if (!this.props.doNotChangeUrl && item.story) {
      let storyPath = `/${item.story.slug}`;
      if (item.story.url) {
        try {
          storyPath = new URL(item.story.url, "https://placeholder.local").pathname;
        } catch (e) {
          storyPath = `/${item.story.slug}`;
        }
      }
      const metaTitle = get(item, ["story", "seo", "meta-title"], item.story.headline);
      const title = get(item, ["customSeo", "title"], metaTitle);
      const path = this.props.changeUrlTo ? this.props.changeUrlTo(item) : storyPath;
      if (global.app && typeof global.app.maybeSetUrl === "function") {
        global.app.maybeSetUrl(path, title);
      }
    }

    // 3. Trigger onItemFocus (video player sync, maySetGlobals, etc.)
    if (this.props.onItemFocus) {
      this.props.onItemFocus(item, index);
    }

    // 4. Initial focus analytics: run synchronously so the single event: "pageview" fires
    // with location.pathname, document.title, author, and category all 100% in sync
    if (isInitialFocus && this.props.onInitialItemFocus) {
      this.props.onInitialItemFocus(item, index);
    }
  }

  loadMore() {
    const maxStories = this.props.maxStories || 5;
    const currentTotal = this.allItems().length;

    // Hard ceiling: if we've already reached 5 stories or loading or hasMore is false, stop
    if (this.state.loading || !this.state.hasMore || currentTotal >= maxStories) {
      if (this.state.hasMore) {
        this.terminateLoadObserver();
        this.setState({ hasMore: false });
      }
      return;
    }

    const remainingSlots = maxStories - currentTotal;
    const pageNumber = this.state.pageNumber;
    const story = get(this.props.data, ["story"], {});

    this.setState(
      {
        loading: true,
        pageNumber: pageNumber + 1,
      },
      () => {
        if (!this.props.loadItems) {
          this.terminateLoadObserver();
          this.setState({ loading: false, hasMore: false });
          return;
        }

        this.props
          .loadItems(pageNumber, story, this.props.config, { seenStoryIds: this.state.seenStoryIds })
          .then((items) => {
            if (!items || !items.length) {
              this.terminateLoadObserver();
              this.setState({
                loading: false,
                hasMore: false,
              });
              return;
            }

            // Deduplicate incoming stories against already loaded items + deduplicate within batch
            const existingIds = new Set(
              this.allItems()
                .map((item) => item?.story?.id || item?.id)
                .filter(Boolean)
            );

            const uniqueNewItems = [];
            for (const item of items) {
              const id = item?.story?.id || item?.id;
              if (id && !existingIds.has(id)) {
                existingIds.add(id);
                uniqueNewItems.push(item);
              }
            }

            if (!uniqueNewItems.length) {
              // All items returned were duplicates; conclude infinite scroll
              this.terminateLoadObserver();
              this.setState({
                loading: false,
                hasMore: false,
              });
              return;
            }

            // Limit items to fit within remainingSlots so total never exceeds maxStories
            const itemsToAdd = uniqueNewItems.slice(0, remainingSlots);
            const nextMoreItems = this.state.moreItems.concat(itemsToAdd);
            const newTotal = 1 + nextMoreItems.length;
            const reachedLimit = newTotal >= maxStories;

            if (reachedLimit) {
              this.terminateLoadObserver();
            }

            this.setState({
              loading: false,
              moreItems: nextMoreItems,
              hasMore: !reachedLimit,
            });
          })
          .catch((err) => {
            console.error("Error loading infinite scroll stories:", err);
            this.terminateLoadObserver();
            this.setState({
              loading: false,
              hasMore: false,
            });
          });
      }
    );
  }

  render() {
    const items = this.allItems();
    const itemObservers = this.focusObserver
      ? this.loadObserver
        ? [this.focusObserver, this.loadObserver]
        : [this.focusObserver]
      : this.loadObserver
        ? [this.loadObserver]
        : [];

    return (
      <div>
        {items.map((data, index) => (
          <ScrollItem
            key={data?.story?.id || index}
            index={index}
            data={data}
            render={this.props.render}
            observers={itemObservers}
            minHeight={this.props.minHeight || 50}
          />
        ))}
        {this.state.hasMore && this.loadObserver && <ScrollLoadMore observer={this.loadObserver} />}
      </div>
    );
  }
}

InfiniteStoryBase.propTypes = {
  data: PropTypes.object.isRequired,
  render: PropTypes.func.isRequired,
  loadItems: PropTypes.func,
  onItemFocus: PropTypes.func,
  onInitialItemFocus: PropTypes.func,
  doNotChangeUrl: PropTypes.bool,
  changeUrlTo: PropTypes.func,
  loadMargin: PropTypes.string,
  focusCallbackAt: PropTypes.number,
  neverHideItem: PropTypes.bool,
  maxStories: PropTypes.number,
  minHeight: PropTypes.number,
  config: PropTypes.object,
};

InfiniteStoryBase.defaultProps = {
  maxStories: 5,
  neverHideItem: true,
  focusCallbackAt: 20,
};
